import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit, ChangeDetectorRef, Component, computed, effect, inject, OnDestroy, Signal, ViewChild,
} from '@angular/core';
import { FormGroup, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule, ThemePalette } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, NavigationEnd, NavigationStart, Params, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { addHours, subHours } from 'date-fns';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { catchError, forkJoin, map, of, Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { TrackerConfigTool } from 'src/app/classes/shared/tracker-config.tool';
import { Pop } from 'src/app/classes/shared/pop';
import { Tools } from 'src/app/classes/shared/tools';
import { TrackerTool } from 'src/app/classes/shared/tracker.tools';
import { UiTool } from 'src/app/classes/shared/ui.tools';
import { DeleteModalComponent } from 'src/app/components/shared/dialogs/delete-modal/delete-modal.component';
import { TrackerPositionComponent } from 'src/app/components/shared/tracker-position/tracker-position.component';
import { Group } from 'src/app/interfaces/api/group';
import { Intervention } from 'src/app/interfaces/api/intervention';
import { Project } from 'src/app/interfaces/api/project';
import { PutTracker, Tracker } from 'src/app/interfaces/api/tracker';
import { TrackerConfig } from 'src/app/interfaces/api/trackerConfig';
import { defaultUnit, Unit } from 'src/app/interfaces/api/unit';
import { User } from 'src/app/interfaces/api/user';
import { GroupService } from 'src/app/services/api/groups/group.service';
import { InterventionService } from 'src/app/services/api/interventions/intervention.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { UnitService } from 'src/app/services/api/units/unit.service';
import { UserService } from 'src/app/services/api/users/user.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { ButtonComponent, ChipComponent, SearchBarComponent, SpinnerComponent } from 'ui-library';
import { WebSocketService } from 'src/app/services/core/web-socket.service';
import { TimeFormatPipe } from "../../../pipes/time-format.pipe";
import { ResourcesModalComponent } from './resources-modal/resources-modal.component';
import { TrackerConfigFormComponent } from './tracker-config-form/tracker-config-form.component';
import { TrackersModalComponent } from './trackers-modal/trackers-modal.component';
import { TemplatePickerDialogComponent } from 'src/app/components/shared/dialogs/template-picker-dialog/template-picker-dialog.component';
import { TrackerTemplateEditor } from 'src/app/classes/shared/tracker-template-editor';
import { KydBulkConfigDialogComponent } from '../kyd/kyd-bulk-config-dialog/kyd-bulk-config-dialog.component';
import { KydConfigDialogComponent } from '../kyd/kyd-config-dialog/kyd-config-dialog.component';
import { KydConfigureChoiceDialogComponent } from '../kyd/kyd-configure-choice-dialog/kyd-configure-choice-dialog.component';
import { TrackerSettingsOverviewDialogComponent } from 'src/app/components/shared/dialogs/tracker-settings-overview-dialog/tracker-settings-overview-dialog.component';

@Component({
  selector: 'app-resources',
  imports: [
    MatTableModule,
    FormsModule,
    ButtonComponent,
    MatTabsModule,
    MatButtonModule,
    MatInputModule,
    CommonModule,
    TimeFormatPipe,
    TranslateModule,
    MatIconModule,
    MatPaginatorModule,
    MatOptionModule,
    MatDividerModule,
    MatSelectModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatToolbarModule,
    MatTooltipModule,
    MatSort,
    MatAccordion,
    MatExpansionModule,
    SearchBarComponent,
    MatCardModule,
    MatCheckboxModule,
    NoDataComponent,
    SpinnerComponent,
    ChipComponent,
    TrackerConfigFormComponent,
  ],
  templateUrl: './resources.component.html',
  styleUrl: './resources.component.scss',
  standalone: true,
  animations: [
    trigger('slideInOut', [
      state('open', style({
        transform: 'translateX(0)',
      })),
      state('closed', style({
        transform: 'translateX(100%)',
      })),
      transition('open <=> closed', [
        animate('300ms ease-in-out')
      ]),
    ])
  ]
})

export class ResourcesComponent implements AfterViewInit, OnDestroy {
  // Services
  private readonly userService: UserService = inject(UserService);
  private readonly auth: AuthService = inject(AuthService);
  private readonly unitService: UnitService = inject(UnitService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly trackerService: TrackerService = inject(TrackerService);
  private readonly interventionService: InterventionService = inject(InterventionService);
  private readonly groupService: GroupService = inject(GroupService)
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly tools: Tools = inject(Tools);
  private readonly trackerTools: TrackerTool = inject(TrackerTool);
  private readonly pop: Pop = inject(Pop);
  private readonly trackerTool: TrackerTool = inject(TrackerTool);
  private readonly trackerConfigTool: TrackerConfigTool = inject(TrackerConfigTool);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly uiTool: UiTool = inject(UiTool);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly webSocketService: WebSocketService = inject(WebSocketService);

  // Component API
  @ViewChild('trackerPaginator') trackerPaginator!: MatPaginator;
  @ViewChild('unitPaginator') unitPaginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('trackerSort') trackerSort!: MatSort;
  @ViewChild('picker') picker: any;
  @ViewChild('picker1') picker1: any;

  // Variables
  loading: boolean = false;
  units: any[] = [];
  currentUnit: any = defaultUnit;
  currentTracker: Tracker = {} as Tracker;
  unitsCount: Number = 0;
  unitsData: MatTableDataSource<Unit> = new MatTableDataSource<Unit>();
  unitsColumns: string[] = ['status', 'name', 'type', 'trackers', 'actions'];
  // Sort-order dropdowns, one combined "field-direction" value per tab - mirrors
  // unifield-mobile-2's devices.page.ts (same field names/defaults) so both apps offer the
  // same sort options. Defaults to most-recently-created first, or whatever the user picked
  // last (see onUnitSortOrderChange()/onTrackerSortOrderChange()) - persisted in localStorage
  // (per-browser, like everything else on this tab) so it survives a reload/revisit.
  private static readonly UNIT_SORT_ORDER_STORAGE_KEY = 'resources_unit_sort_order';
  private static readonly TRACKER_SORT_ORDER_STORAGE_KEY = 'resources_tracker_sort_order';
  unitSortOrder: string = localStorage.getItem(ResourcesComponent.UNIT_SORT_ORDER_STORAGE_KEY) || 'created-desc';
  trackerSortOrder: string = localStorage.getItem(ResourcesComponent.TRACKER_SORT_ORDER_STORAGE_KEY) || 'created-desc';
  trackersColumns: string[] = ['select', 'status', 'name', 'resource', 'serial', 'lastUpdate', 'actions'];
  loadingUnits: Boolean = false;
  isRateLimitReached: Boolean = false;
  trackers: any[] = [];
  trackersCount: Number = 0;
  trackersData: MatTableDataSource<Tracker> = new MatTableDataSource<Tracker>();
  loadingTrackers: Boolean = false;
  // Multi-select / bulk actions on the "Dispositifs" tab - mirrors kyd.component.ts's own
  // identically-shaped multiSelectMode (Tracker.selected is the same front-only field it uses).
  // "Appliquer un modèle"/"Modifier" only make sense for KYD, so both
  // silently narrow the selection to KYD trackers rather than acting on the whole one - "Voir
  // les paramètres"/"Supprimer" work for any type.
  multiSelectMode: boolean = false;
  unitsForm: FormGroup = new FormGroup({});
  colors: any[] = [];
  unitTypeArray: any[] = [];
  usedTrackers: any[] = [];
  availableTrackers: any[] = [];
  usedTrackerIds: string[] = [];
  unitNames: string[] = [];
  chart: any = {};
  errorName = false;
  // currentTracker is a direct reference into this.trackers (see editItem()), not a copy - the
  // name input's own two-way binding already mutates it (and so the table row behind it) on
  // every keystroke, regardless of whether updateTracker() actually goes on to save it. Without
  // a separate "last confirmed" value to fall back to, rejecting an invalid KYD rename (see
  // updateTracker()) left that rejected name sitting in the field/table looking accepted, even
  // though the backend never received it.
  private lastConfirmedTrackerName: string = '';
  isCreateUnit = false;
  pictureName: any;
  pictureUrl: any;
  unitIcons: { name: string; value: string; icon: string; }[] = [];
  time = new Date();
  format = 24;
  days: any[] = [];
  measures: any[] = [];
  schedule: { [key: string]: string[] } = {};
  isSelectIcon = false;
  isUnitTabsSettings = true;
  startDate: string = '';
  endDate: string = '';
  sidepanelState: string = "closed";
  // See closeSidepanelSilently(): true only for the one forced flush that snaps the sidepanel
  // straight to 'closed' before this view gets detached by the router, so no transition plays.
  sidepanelAnimationDisabled: boolean = false;
  // See remountSidepanel(): briefly toggled false→true on every reattachment to force a fresh DOM
  // element for the sidepanel's *ngIf block, clearing the animation engine's stale bookkeeping for
  // the previous (reused-by-detach) element.
  sidepanelMounted: boolean = true;
  sidepanelType: 'unit' | 'tracker' = "unit";
  groupData: MatTableDataSource<any> = new MatTableDataSource<any>();

  public date: Date = new Date();
  public disabled = false;
  public showSpinners = true;
  public showSeconds = false;
  public touchUi = false;
  public enableMeridian = false;
  public minDate: Date = new Date();
  public maxDate: Date = addHours(new Date(), 2);
  public stepHour = 1;
  public stepMinute = 1;
  public stepSecond = 1;
  accuracies: any[] = [];
  distance = 0;
  lastEventTrackerId: any;
  public color: ThemePalette = 'primary';
  dynamicFields = ["x", "y", "z", "lat", "lon"];

  typeArray: any = {
    fr: [
      { name: "Equipement", value: "equipment" },
      { name: "Opérateur", value: "worker" },
      { name: "Matériel", value: "material" }
    ]
  };

  pictureArray = [
    { title: "worker", value: "worker.jpg" },
    { title: "Hook", value: "hook.jpg" },
    { title: "Formwork", value: "formwork.jpg" },
    { title: "Wheel", value: "wheel.jpg" },
    { title: "Antenna", value: "antenna.jpg" },
    { title: "Drone", value: "drone.jpg" },
    { title: "Wall", value: "wall.jpg" },
    { title: "Custom", value: "custom.jpg" }
  ];
  
  unitAttachments: any[] = [];
  showUnit = false;
  dayEventsArray: any[] = [];
  weekAndMonthEventsArray: any[] = [];
  userDefinedEventsArray: any[] = [];
  eventsArray: any[] = [];
  tabIndex: number = 0;
  destroy$ = new Subject<void>();
  socketTrackSub: Subscription = new Subscription();
  updatedFields: any = {};
  updateHeight: Record<string, boolean> = {};
  isMobile!: boolean;
  configs: TrackerConfig[] = [];
  // The currently-open tracker's own settings and template, edited directly (see saveDeviceDraft()).
  readonly deviceEditor = new TrackerTemplateEditor();
  // Interventions linked to the currently-open Dispositif (Maintenance panel), fetched via
  // Intervention.tracker rather than through InterventionStore — that store's filters/pagination
  // are shared global state owned by the Interventions tab and must not be mutated from here.
  linkedInterventions: Intervention[] = [];
  loadingLinkedInterventions: boolean = false;
  // Dispositif id to auto-open once its data has loaded, coming back from the Interventions
  // drawer's `?tracker=` deep-link (see constructor and getData()).
  private pendingTrackerId: string | null = null;

  // Signals
  readonly currentProject: Signal<Project> = this.projectService.currentProjectSignal;
  readonly user: Signal<User> = this.auth.currentUser;

  // Computed
  readonly canEditResources = computed(() => {
    const role = this.user()?.role?.name;
    return role !== 'guest';
  });

  readonly isDark = computed<boolean>(() => {
    return this.user()?.theme === 'dark';
  });

  readonly tableHeaderClass = computed(() =>
    this.isDark() ? 'dark_th' : 'light_th'
  );

  constructor() {
    let tabIndex = this.route.snapshot.queryParamMap.get('tab');

    if (tabIndex)
      this.tabIndex = parseInt(tabIndex);

    // Driven by Router.events rather than a one-time `route.snapshot` read in this constructor (or
    // a `queryParamMap` subscription, which turns out not to re-emit here either — confirmed live):
    // this app's custom RouteReuseStrategy (see reuse-strategy.ts) DETACHES this component instead
    // of destroying it when navigating away, and REATTACHES that same instance — without ever
    // re-running this constructor — whenever the query params match a previously-visited
    // combination exactly. Coming back here a second time for the same Dispositif (cancel a second
    // linked creation, or link a second intervention) reproduces the exact same `?tab=1&tracker=X`
    // URL, so a one-time read would silently do nothing on that second visit. NavigationEnd fires
    // on every completed navigation regardless of component reuse, and `route.snapshot` is safe to
    // re-read fresh once it does.
    this.processTrackerDeepLink();
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      // Confirmed live: from the SECOND reattachment onward, the sidepanel's opening transition
      // stops playing entirely (it just snaps open) — every time, not just once. A raw
      // ViewContainerRef.detach() (what the RouteReuseStrategy uses) removes the DOM node without
      // going through the :leave-aware teardown `*ngIf` normally does, so the animation engine's
      // per-element bookkeeping for that exact, reused DOM node is never cleared across detach —
      // and it only gets more confused with each further detach/reattach cycle. Forcing a genuine
      // destroy + recreate of the panel's `*ngIf` block on every reattachment gives it a brand new
      // element with a clean registration each time, instead of reusing the increasingly stale one.
      this.remountSidepanel();
      this.processTrackerDeepLink();
    });

    // Mirrors the same fix in InterventionsComponent: this sidepanel is always mounted too (see
    // `*ngIf="canEditResources()"`, which stays true for the whole session once an editor lands
    // here), so leaving it 'open' on this detached-not-destroyed view left a stale animation-engine
    // baseline that (a) replayed a stray closing transition on return, and (b) is the suspected
    // cause of the FIRST open transition breaking (or drawers failing to open at all) the next time
    // any drawer/sidepanel animates anywhere, since Angular's animation engine is shared app-wide
    // and this trigger shares the same 'slideInOut' name. Closing it before the router detaches
    // this view (rather than leaving that to whatever happens on return) guarantees landing back on
    // the plain table with no leftover open panel.
    this.router.events.pipe(
      filter((event): event is NavigationStart => event instanceof NavigationStart),
      takeUntil(this.destroy$),
    ).subscribe(event => {
      if (this.sidepanelState === 'open' && !event.url.startsWith('/project/project-settings/resources')) {
        this.closeSidepanelSilently();
      }
    });

    // Effect: Changement de projet
    effect(() => {
      const project = this.currentProject();

      if (project?.id) {
        this.getData();
      }
    });

    if (this.user()) {
      this.translate.setDefaultLang(this.user().language);
    } else {
      this.translate.setDefaultLang(this.userService.initLanguage());
    }

    this.watchLiveTrackerDeletes();
  }

  // getData()'s own live-tracker chain (getUnits() -> getConfigs() -> getTrackers(), all three
  // themselves concat(HTTP, socket) streams) re-derives trackersCount from trackersData on every
  // re-emission (see the comment inside that block) - in principle that should already cover a
  // tracker deleted from elsewhere (e.g. the mobile app), but that chain re-subscribes itself
  // from scratch on every live Unit/TrackerConfig update too (getData() re-runs whole on project
  // change, and each of its own three nested subscribes stacks a further one on every parent
  // re-emission), which made it unreliable in practice - the paginator's total kept getting left
  // stale after a mobile-side delete. getUpdateRaw() sidesteps all of that entirely: it's the raw
  // backend broadcast, independent of the local-storage cache reconciliation getUpdate() applies
  // and of getData()'s own subscription tree, so a delete is reflected here regardless of what
  // state that other chain happens to be in. Subscribed once for the lifetime of this component
  // (not re-run from getData()) since it doesn't depend on which project/unit/config data is
  // currently loaded - only interested in removing whatever tracker id was just deleted.
  private watchLiveTrackerDeletes() {
    this.webSocketService.getUpdateRaw('Tracker').pipe(takeUntil(this.destroy$)).subscribe((msg: any) => {
      if (msg?.method !== 'DELETE' || !msg.data?.length) return;

      const deletedId = msg.data[0]?._id;
      if (!deletedId) return;

      const index = this.trackers.findIndex(t => t.id === deletedId);
      if (index < 0) return;

      this.trackers.splice(index, 1);
      this.trackersData = new MatTableDataSource(this.trackers);
      this.trackersData.sort = this.trackerSort;
      this.trackersData.paginator = this.trackerPaginator;
      this.trackersCount = this.trackersData.filteredData.length;
      this.cdr.detectChanges();
    });
  }

  ngAfterViewInit(): void {
    this.uiTool.isMobile$.subscribe(value => {
      this.isMobile = value;
    });

    this.accuracies = [];
    this.chart = {};

    this.translate.get('home').pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.colors = [{ name: "Danger (" + this.translate.instant('colors.red') + ")", value: "#dd4b39" },
      { name: "Warning (" + this.translate.instant('colors.orange') + ")", value: "#FF6600" },
      { name: "Primary (" + this.translate.instant('colors.blue') + ")", value: "#3c8dbc" },
      { name: "Success (" + this.translate.instant('colors.green') + ")", value: "#00a65a" },
      { name: "Lemon(" + this.translate.instant('colors.yellow') + ")", value: "#f3e612" },
      { name: "Teal (" + this.translate.instant('colors.teal') + ")", value: "#39CCCC" },
      { name: "Fuchsia (" + this.translate.instant('colors.purple') + ")", value: "#F012BE" },
      { name: "Info (" + this.translate.instant('colors.lightGrey') + ")", value: "#DDDDDD" },
      { name: "Dark (" + this.translate.instant('colors.black') + ")", value: "#333333" },
      { name: "Muted (" + this.translate.instant('colors.grey') + ")", value: "#808080" },
      { name: this.translate.instant('colors.white'), value: "#FFFFFF" },
      { name: this.translate.instant('colors.custom'), value: "custom" }];

      this.unitTypeArray = [
        { name: this.translate.instant('units.worker'), value: "worker" },
        { name: this.translate.instant('units.equipment'), value: "equipment" },
        { name: this.translate.instant('units.material'), value: "material" },
      ]

      this.unitIcons = [
        { name: this.translate.instant('units.worker'), value: "worker", icon: "worker.jpg" },
        { name: "Mobile machinery (bulldozer,truck,forklift)", value: "equipment", icon: "wheel.jpg" },
        { name: "Crane hook", value: "equipment", icon: "hook.jpg" },
        { name: "Drone", value: "equipment", icon: "drone.jpg" },
        { name: this.translate.instant('units.material'), value: "material", icon: "formwork.jpg" },
        { name: this.translate.instant('units.antenna'), value: "antenna", icon: "antenna.jpg" }
      ];

      this.days = [
        { name: this.translate.instant('date.monday'), value: 'mon' },
        { name: this.translate.instant('date.tuesday'), value: 'tue' },
        { name: this.translate.instant('date.wednesday'), value: 'wed' },
        { name: this.translate.instant('date.thursday'), value: 'thu' },
        { name: this.translate.instant('date.friday'), value: 'fri' },
        { name: this.translate.instant('date.saturday'), value: 'sat' },
        { name: this.translate.instant('date.sunday'), value: 'sun' },
      ]

      this.measures = [
        { name: this.translate.instant('date.hour'), value: "hour" },
        { name: "kg", value: "kg" },
        { name: this.translate.instant('measure.meter'), value: "m" },
        { name: "m2", value: "m2" },
        { name: "m3", value: "m3" },
        { name: this.translate.instant('measure.litre'), value: "l" },
        { name: this.translate.instant('measure.batch'), value: "batch" }
      ];

      this.startDate = subHours(new Date, 1).toISOString();
      this.endDate = new Date().toISOString();
    });
  }

  ngOnDestroy(): void {
    if (this.socketTrackSub)
      this.socketTrackSub.unsubscribe();

    this.socketTrackSub = new Subscription();

    this.destroy$.next();
    this.destroy$.complete();
  }

  // See the comment above the call site in the constructor: re-reads `route.snapshot` fresh on
  // every completed navigation to this route (initial load AND every reattachment alike), instead
  // of once.
  private processTrackerDeepLink(): void {
    // Value is a Tracker.uuid (not the Mongo _id) — interventions link devices by uuid.
    const trackerUuid = this.route.snapshot.queryParamMap.get('tracker');
    if (!trackerUuid) return;

    if (this.route.snapshot.queryParamMap.get('linked') === 'success') {
      this.pop.toast(this.translate.instant('resources.tracker_intervention_linked_success'), 'success', 4000, 'link');
    }

    // Trackers may already be loaded from an earlier visit to this same (reattached) instance —
    // in that case open immediately rather than waiting for a getData() that may never rerun
    // (the "changement de projet" effect only fires when the project id actually changes).
    const alreadyLoadedTracker = this.trackers.find(t => t.uuid === trackerUuid);
    if (alreadyLoadedTracker) {
      this.editItem(alreadyLoadedTracker, 'tracker');
    } else {
      this.pendingTrackerId = trackerUuid;
    }

    // One-time triggers: drop them from the URL so a manual refresh doesn't re-show the toast or
    // re-open the panel. This is itself a same-route, query-params-only navigation (not a
    // detach/reattach), so it correctly re-triggers this same handler once more — harmlessly, since
    // `trackerId` is now null and the guard above returns immediately.
    this.router.navigate([], { queryParams: { tracker: null, linked: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  toggleSidepanel() {
    this.sidepanelState = this.sidepanelState === 'open' ? 'closed' : 'open';
  }

  // Merely flipping `sidepanelState` is not enough: the panel is always mounted, and Angular's
  // animation engine only updates its last-known state for an element while it's still attached
  // and change-detected — the RouteReuseStrategy detaches this whole view (DOM removal) without
  // ever flushing that update, so the engine's remembered state stays 'open' regardless of when the
  // property changed. Disabling the trigger for one forced, synchronous detectChanges() (still
  // fully attached at this point — the router only detaches later, once route resolution for the
  // target completes) makes the engine snap straight to 'closed' instead of queuing a transition
  // that would otherwise still be pending/replayed on the next reattach.
  private closeSidepanelSilently(): void {
    this.sidepanelAnimationDisabled = true;
    this.sidepanelState = 'closed';
    this.cdr.detectChanges();

    // Re-enabled right after so a normal, user-triggered open/close on this same instance (once
    // reattached and revisited) still animates — this flag must only suppress the one forced close.
    Promise.resolve().then(() => { this.sidepanelAnimationDisabled = false; });
  }

  // Two synchronous detectChanges() calls: the first tears the *ngIf'd panel down completely
  // (normal structural-directive destroy, which properly clears the animation engine's
  // per-element bookkeeping — unlike the RouteReuseStrategy's raw detach), the second creates a
  // brand new element for it. sidepanelState is already 'closed' by now (closeSidepanelSilently()
  // ran on the way out), so the fresh element just renders closed with no transition to play.
  private remountSidepanel(): void {
    this.sidepanelMounted = false;
    this.cdr.detectChanges();
    this.sidepanelMounted = true;
    this.cdr.detectChanges();
  }

  getData() {
    this.units = [];
    this.trackers = [];
    this.usedTrackerIds = [];
    this.usedTrackers = [];
    this.unitNames = [];
    this.loading = true;

    this.unitService.getUnitsCount().pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.unitsCount = res;
    })

    this.trackerService.getTrackersCountByDatabase().pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.trackersCount = res;
    })

    this.unitService.getUnits().pipe(takeUntil(this.destroy$)).subscribe((units: Unit[]) => {
      const savedCurrentUnitId = this.currentUnit?.id;

      this.trackerService.getConfigs().pipe(takeUntil(this.destroy$)).subscribe((configs: TrackerConfig[]) => {
        this.configs = configs;
        this.deviceEditor.configs = configs;
        this.trackerTool.setConfigs(configs);
        this.trackerService.getTrackers().pipe(takeUntil(this.destroy$)).subscribe((trackers: Tracker[]) => {
          this.trackers = trackers;

          // getTrackers() stays open for live socket updates (see TrackerService), so this
          // callback re-runs whenever ANY tracker changes on the backend - e.g. the mobile
          // app confirming/declining a pending KYD config. `this.trackers` gets a fresh array
          // each time, but `currentTracker` (open in the sidepanel) is a separate reference
          // into the OLD array and would otherwise go stale until the panel is reopened -
          // exactly why the "en attente" banner used to linger after the app had already
          // resolved it. Only the backend-authoritative fields are merged in place (not a
          // wholesale reference swap), so a name/version/etc. edit the user is mid-typing
          // here isn't discarded by an unrelated update to some other tracker.
          if (this.sidepanelType === 'tracker' && this.currentTracker?.id) {
            const freshCurrentTracker = trackers.find(t => t.id === this.currentTracker.id);

            if (freshCurrentTracker) {
              const settingsChanged = JSON.stringify(this.currentTracker.settings) !== JSON.stringify(freshCurrentTracker.settings);
              const offlineDelayChanged = this.currentTracker.offlineDelay !== freshCurrentTracker.offlineDelay;
              const configChanged = this.trackerConfigTool.configIdOf(this.currentTracker) !== this.trackerConfigTool.configIdOf(freshCurrentTracker);

              this.currentTracker.settings = freshCurrentTracker.settings;
              this.currentTracker.settingsConfirmed = freshCurrentTracker.settingsConfirmed;
              this.currentTracker.config = freshCurrentTracker.config;
              this.currentTracker.connecting = freshCurrentTracker.connecting;
              this.currentTracker.connectingAt = freshCurrentTracker.connectingAt;
              this.currentTracker.offlineDelay = freshCurrentTracker.offlineDelay;

              // Re-derives the form's starting values from the fresh settings/config - e.g. the
              // mobile app just wrote a KYD's real settings. Only when it actually changed, so an
              // unrelated update (another tracker, this one's `connecting` flag...) never
              // discards fields the user is mid-editing here.
              if (settingsChanged || offlineDelayChanged || configChanged) {
                this.refreshDeviceDraft();
              }
            } else {
              // Deleted from elsewhere (the mobile app, or another web session) while this
              // exact tracker's panel was open here - mirrors kyd-config-dialog.component.ts's
              // own handling of the same live-update mechanism, for the KYD tab's editor.
              // Without this, saving here (updateTracker()/saveDeviceDraft()) would just 404
              // against an id that no longer exists.
              this.pop.warning(this.translate.instant('trackers.deleted_elsewhere'));
              this.sidepanelState = 'closed';
              this.currentTracker = {} as Tracker;
            }
          }

          this.trackers.forEach(t => {
            let isDelayOk = this.tools.isWithinOfflineDelay(t, configs);
            t.colorName = isDelayOk ? "green" : "red";
          });

          units.forEach((newUnit: Unit) => {
            if (!newUnit.lastPosition) {
              newUnit.lastPosition = {
                x: 0,
                y: 0,
                z: 0,
                lat: 0,
                lon: 0
              }
            }

            if (!newUnit.offset) {
              newUnit.offset = {
                x: 0,
                y: 0,
                z: 0
              }
            }

            if (!newUnit.collider) {
              newUnit.collider = {
                geometry: 'none'
              }
            }

            let unitTrackers = this.trackers.filter(t => newUnit.trackers.find(tracker => typeof tracker == "string" ? tracker == t.id : tracker.id == t.id))

            newUnit.colorName = unitTrackers.find(t => t.colorName == "green") ? "green" : "red";
            newUnit.trackers = unitTrackers;
          });

          if (this.units.length === 0) {
            this.units = units;
          } else {
            units.forEach(newUnit => {
              const existingIndex = this.units.findIndex(u => u.id === newUnit.id);

              if (existingIndex >= 0) {
                this.units.splice(existingIndex, 1, newUnit);
              } else {
                this.units.push(newUnit);
              }
            });

            this.units = this.units.filter(u => units.find(nu => nu.id === u.id));
          }

          this.unitNames = [];

          this.units.forEach(unit => {
            this.unitNames.push(unit.name.trim().toLowerCase());
          });

          // Re-applied on every load (including live socket re-emissions, which rebuild these
          // arrays/data sources from scratch each time) so the currently-chosen sort order
          // survives instead of reverting to server/insertion order.
          this.units = this.sortUnitsArray(this.units);
          this.trackers = this.sortTrackersArray(this.trackers);

          this.unitsData = new MatTableDataSource(this.units);
          this.trackersData = new MatTableDataSource(this.trackers);

          // trackersCount used to only ever be set once, from the one-shot
          // getTrackersCountByDatabase() call above - unlike this.trackers/trackersData, which
          // already re-run on every live socket update (see the comment above), so the
          // paginator's total silently went stale after any tracker was added/deleted from
          // elsewhere (e.g. the mobile app). Deriving it from the same live data this callback
          // already rebuilds every time costs nothing extra - no new subscription, no polling.
          this.trackersCount = this.trackersData.filteredData.length;

          // This whole block also runs from a raw socket event (a tracker added/removed from
          // another client, e.g. the mobile app) - unlike an HTTP response or a user click,
          // there's nothing else in that stack forcing Angular to actually flush a change
          // detection pass afterwards, which is what left mat-paginator's "sur X" total (bound
          // to trackersCount, just reassigned above) visually stuck at its old value even
          // though the property itself was correct. Mirrors this component's own
          // closeSidepanelSilently()/remountSidepanel(), which need the same forced,
          // synchronous flush for the exact same reason.
          this.cdr.detectChanges();

          this.unitsData.sort = this.sort;
          this.unitsData.paginator = this.unitPaginator;
          this.trackersData.sort = this.trackerSort;
          this.trackersData.paginator = this.trackerPaginator;

          if (savedCurrentUnitId) {
            const restoredUnit = this.units.find(u => u.id === savedCurrentUnitId);

            if (restoredUnit) {
              if (this.currentUnit !== restoredUnit) {
                this.currentUnit = restoredUnit;
              }
            } else {
              if (this.units.length > 0) {
                this.currentUnit = this.units[0];
              }
            }
          } else if (this.units.length > 0) {
            this.currentUnit = this.units[0];
          }

          this.lastEventTrackerId = this.units[0];
          if (this.currentUnit) {
            this.initSchedule();
            this.selectAvalaibleTrackers();
          }

          this.loading = false;

          // Fallback for the case processTrackerDeepLink() above fired before trackers had
          // loaded (e.g. the very first load of this component instance).
          if (this.pendingTrackerId) {
            const tracker = this.trackers.find(t => t.uuid === this.pendingTrackerId);
            if (tracker) this.editItem(tracker, 'tracker');
            this.pendingTrackerId = null;
          }
        })
      })
    })
  }

  onTabChanged(event: any) {
    event.tab.textLabel == this.translate.instant('units.title') ? this.isUnitTabsSettings = true : this.isUnitTabsSettings = false;
    const queryParams: Params = { tab: event.index };

    this.router.navigate(
      [],
      {
        queryParams: queryParams,
        queryParamsHandling: 'merge',
      });

  }

  applyFilter(search: string) {
    if (this.isUnitTabsSettings) {
      this.unitsData.filter = search.trim().toLowerCase();

      const filteredCount = this.unitsData.filteredData.length;
      this.unitsCount = filteredCount

      if (this.unitsData.paginator) {
        this.unitsData.paginator.firstPage();
      }
    } else {
      this.trackersData.filter = search.trim().toLowerCase();

      const filteredCount = this.trackersData.filteredData.length;
      this.trackersCount = filteredCount

      if (this.trackersData.paginator) {
        this.trackersData.paginator.firstPage();
      }
    }
  }

  trackersFilter(search: string) {
    this.trackersData.filter = search.trim().toLowerCase();
    const filteredCount = this.trackersData.filteredData.length;
    this.trackersCount = filteredCount

    if (this.trackersData.paginator) {
      this.trackersData.paginator.firstPage();
    }
  }

  // A Mongo ObjectId's first 4 bytes are a Unix timestamp (seconds) of when it was created -
  // Tracker has no explicit createdDate field of its own (see unifield-api-v3's tracker.js
  // schema) - Unit does (see sortUnitsArray() below), so this is only needed for trackers.
  private trackerCreatedAt(tracker: any): number {
    const id = tracker?.id;
    return typeof id === 'string' && id.length >= 8 ? parseInt(id.substring(0, 8), 16) * 1000 : 0;
  }

  // getData() re-creates trackersData/unitsData as a brand new MatTableDataSource on every
  // load (including every live socket re-emission - see TrackerService.getTrackers()), so the
  // chosen order is re-applied to the underlying array there each time rather than relying on
  // MatSort state that wouldn't survive that recreation.
  private sortTrackersArray(trackers: Tracker[]): Tracker[] {
    const [field, direction] = this.trackerSortOrder.split('-');
    const factor = direction === 'desc' ? -1 : 1;

    return [...trackers].sort((a, b) => {
      if (field === 'created') return factor * (this.trackerCreatedAt(a) - this.trackerCreatedAt(b));
      if (field === 'lastMessage') return factor * (new Date(a.lastUpdate || 0).getTime() - new Date(b.lastUpdate || 0).getTime());
      return factor * (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
    });
  }

  private sortUnitsArray(units: any[]): any[] {
    const [field, direction] = this.unitSortOrder.split('-');
    const factor = direction === 'desc' ? -1 : 1;

    return [...units].sort((a, b) => {
      if (field === 'created') return factor * (new Date(a.createdDate || 0).getTime() - new Date(b.createdDate || 0).getTime());
      if (field === 'type') return factor * (a.type || '').toLowerCase().localeCompare((b.type || '').toLowerCase());
      if (field === 'tracker') {
        const aName = (a.trackers?.[0]?.name || '').toLowerCase();
        const bName = (b.trackers?.[0]?.name || '').toLowerCase();
        return factor * aName.localeCompare(bName);
      }
      return factor * (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
    });
  }

  onTrackerSortOrderChange(value: string) {
    this.trackerSortOrder = value;
    localStorage.setItem(ResourcesComponent.TRACKER_SORT_ORDER_STORAGE_KEY, value);
    this.trackers = this.sortTrackersArray(this.trackers);
    this.trackersData.data = this.trackers;
  }

  onUnitSortOrderChange(value: string) {
    this.unitSortOrder = value;
    localStorage.setItem(ResourcesComponent.UNIT_SORT_ORDER_STORAGE_KEY, value);
    this.units = this.sortUnitsArray(this.units);
    this.unitsData.data = this.units;
  }

  async editItem(element: any, type: 'unit' | 'tracker') {
    if (type === 'unit') {
      this.currentUnit = element;
      this.sidepanelType = 'unit';
      this.selectAvalaibleTrackers();
      this.initSchedule();
    } else if (type === 'tracker') {
      // A connection attempt from the mobile app is about to (or just did) read/write this
      // tracker's real settings over BLE - opening the editor here at the same moment risks a
      // save landing right as the device's own confirmed state comes back and overwrites it.
      // Mirrors kyd.component.ts's own editKydTracker() guard.
      if (this.trackerConfigTool.isConnecting(element)) {
        this.pop.warning(this.translate.instant('trackers.connecting_blocks_edit'));
        return;
      }

      this.currentTracker = element;
      this.lastConfirmedTrackerName = element.name;
      this.refreshDeviceDraft();

      this.loadLinkedInterventions(this.currentTracker.uuid);

      if (!this.currentTracker.loadingStats && !this.currentTracker.stats) {
        this.currentTracker.loadingStats = true;
        // TODO: remove promise & put observable (or better, make a TrackerStore)
        this.trackerTool.getStats(this.currentTracker, this.destroy$, this.currentProject()?.id).then(tracker => {
          let trackerIndex = this.trackers.findIndex(u => u.id == tracker.id)
          if (trackerIndex >= 0) {
            this.trackers.splice(trackerIndex, 1, tracker);
            this.trackers[trackerIndex].loadingStats = false;
          }
          if (this.currentTracker.id == tracker.id)
            this.currentTracker = tracker; 
        });
      }
        
      this.sidepanelType = 'tracker';
    }

    if (this.sidepanelState != 'open')
      this.sidepanelState = 'open';
  }

  delete(elt: { name: any; id: any; }, type: string) {
    if (type == "unit") {
      const dialogRef = this.dialog.open(DeleteModalComponent, { data: { title: this.translate.instant('menu.resource.title'), name: elt.name } });

      dialogRef.afterClosed().subscribe({
        next: (res) => {
          if (res) {
            this.unitService.removeUnit(elt.id).pipe(takeUntil(this.destroy$)).subscribe({
              next: () => {
                let unitIndex = this.units.findIndex(u => u.id == elt.id)

                if (unitIndex >= 0) {
                  this.units.splice(unitIndex, 1)
                }

                this.unitsData = new MatTableDataSource(this.units);
                this.pop.toast(this.translate.instant('units.delete_unit_success'), 'success', 4000, 'delete');
              },
              error: (err: any) => console.error(err)
            })
          }
        },
        error: (err: any) => {
          console.error(err)
        }
      });
    } else if (type == "tracker") {
      const dialogRef = this.dialog.open(DeleteModalComponent, { data: { title: this.translate.instant('trackers.title'), name: elt.name } });

      dialogRef.afterClosed().subscribe((res) => {
        if (res === true) {
          this.trackerService.deleteTracker(elt.id).pipe(takeUntil(this.destroy$)).subscribe({
            next: (res: any) => {
              let resultKeys = res ? res.map((keyValue: {}) => Object.keys(keyValue)[0]) : [];

              if (resultKeys.indexOf("success") >= 0) {
                let trackerIndex = this.trackers.findIndex(u => u.id == elt.id)

                if (trackerIndex >= 0) {
                  this.trackers.splice(trackerIndex, 1)
                }

                this.trackersData = new MatTableDataSource(this.trackers);
                this.pop.toast(this.translate.instant('trackers.success.removed_tracker'), 'success', 4000, 'delete');
              } else if (resultKeys.indexOf("error") >= 0)
                this.pop.error(this.translate.instant('trackers.error.removing_tracker'));
              else
                this.pop.warning(this.translate.instant("trackers.warning.removing_tracker"));
            },
            error: (err: any) => {
              this.pop.error(this.translate.instant('trackers.error.removing_tracker'))
              console.error(err)
            }
          })
        }
      });
    } else {
      const dialogRef = this.dialog.open(DeleteModalComponent, { data: { title: this.translate.instant('settings.form.group'), name: elt.name } });

      dialogRef.afterClosed().subscribe((res) => {
        if (res == 'true') {
          this.groupService.removeGroup(elt.id).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
              this.groupService.getGroups().pipe(takeUntil(this.destroy$)).subscribe({
                next: (groups: Group[]) => {
                  this.groupData = new MatTableDataSource(groups);
                },
                error: (err: any) => console.error(err)
              })
            },
            error: (err: any) => console.error(err)
          })
        }
      });
    }
  }

  // Multi-select bulk actions on the "Dispositifs" tab - mirrors kyd.component.ts's own
  // identically-named/shaped methods, scoped to `this.trackers` (every device type) instead of
  // a KYD-only subset.
  get selectedTrackersForBulk(): Tracker[] {
    return this.trackers.filter(t => t.selected);
  }

  toggleMultiSelectMode() {
    this.multiSelectMode = !this.multiSelectMode;
    if (!this.multiSelectMode) this.clearTrackerSelection();
  }

  clearTrackerSelection() {
    this.trackers.forEach(t => t.selected = false);
  }

  get allVisibleTrackersSelected(): boolean {
    return this.trackersData.data.length > 0 && this.trackersData.data.every(t => t.selected);
  }

  toggleSelectAllTrackers() {
    const selectAll = !this.allVisibleTrackersSelected;
    this.trackersData.data.forEach(t => t.selected = selectAll);
  }

  endMultiSelectTrackers() {
    this.multiSelectMode = false;
    this.clearTrackerSelection();
  }

  // "Appliquer un modèle" only makes sense for KYD - a mixed selection just
  // silently applies to whichever selected trackers are KYD, same narrowing kyd.component.ts's
  // own version and unifield-mobile-2's devices.page.ts equivalent already do.
  private kydTrackersInSelection(): Tracker[] {
    return this.selectedTrackersForBulk.filter(t => t.type === 'KYD' || t.settings?.type === 'KYD');
  }

  // Gates the "même configuration pour tous" choice: a single shared settings form only makes
  // sense when every selected tracker is the same type (KydBulkConfigDialogComponent renders one
  // type-specific form, it can't represent a KYD and a Unit at once). A mixed selection still
  // gets "un par un", which opens each tracker in its own correctly-typed editor.
  private selectionHasUniformType(trackers: Tracker[]): boolean {
    if (!trackers.length) return false;
    const first = trackers[0]?.type ?? 'Unit';
    return trackers.every(t => (t.type ?? 'Unit') === first);
  }

  // Same "même modèle pour tous" vs "un par un" choice as bulkEditTrackersDirectly() - "each"
  // lets a different template be picked per device instead of forcing the same one on all.
  bulkApplyTemplateDirectly() {
    const trackers = this.kydTrackersInSelection();
    if (!trackers.length) {
      this.pop.warning(this.translate.instant('trackers.no_kyd_in_selection'));
      return;
    }

    const choiceRef = this.dialog.open(KydConfigureChoiceDialogComponent, {
      width: '450px',
      data: { count: trackers.length, context: 'applyTemplate' },
      restoreFocus: false
    });

    choiceRef.afterClosed().subscribe(choice => {
      this.endMultiSelectTrackers();
      if (choice === 'same') this.bulkApplySameTemplate(trackers);
      else if (choice === 'each') this.applyTemplateSequentially([...trackers]);
    });
  }

  private bulkApplySameTemplate(trackers: Tracker[]) {
    this.dialog.open(TemplatePickerDialogComponent, {
      width: '500px',
      data: { typeFilter: 'KYD' },
      restoreFocus: false
    }).afterClosed().subscribe((template: any) => {
      if (!template) return;

      this.pop.confirm(this.translate.instant('trackers.apply_template_confirm', { name: template.name, count: trackers.length }), (confirm: any) => {
        if (!confirm.ok) return;

        const applies = trackers.map(tracker =>
          this.trackerConfigTool.saveSettings(tracker, template.settings, template.offlineDelay, template.id).pipe(
            map(() => true),
            catchError(err => { console.error('[Resources] bulk apply template failed for', tracker.name, err); return of(false); })
          )
        );

        forkJoin(applies).subscribe(results => {
          const failedCount = results.filter(r => !r).length;

          if (failedCount === 0) {
            this.pop.toast(this.translate.instant('units.device.bulk_config_saved', { count: results.length }), 'success', 4000, 'save');
          } else if (failedCount < results.length) {
            this.pop.warning(this.translate.instant('trackers.error.some_kyd_config_failed', { failed: failedCount, total: results.length }));
          } else {
            this.pop.error(this.translate.instant('trackers.error.config_save'));
          }
        });
      });
    });
  }

  // One tracker at a time: opens the template picker again for each, so a different template
  // can be chosen per device - closing the picker without choosing one stops here, same
  // "leaves whatever hasn't been reached yet untouched" convention as configureTrackersSequentially().
  private applyTemplateSequentially(queue: Tracker[]) {
    const next = queue.shift();
    if (!next) return;

    this.dialog.open(TemplatePickerDialogComponent, {
      width: '500px',
      data: { typeFilter: 'KYD', trackerName: next.name },
      restoreFocus: false
    }).afterClosed().subscribe((template: any) => {
      if (!template) return;

      this.trackerConfigTool.saveSettings(next, template.settings, template.offlineDelay, template.id).subscribe({
        next: () => {
          this.pop.toast(this.translate.instant('units.device.config_saved'), 'success', 3000, 'save');
          this.applyTemplateSequentially(queue);
        },
        error: () => this.pop.error(this.translate.instant('trackers.error.config_save')),
      });
    });
  }

  // Same "même configuration pour tous" vs "un par un" choice as kyd.component.ts's own
  // bulkConfigureKyd() - mirrored here exactly rather than skipping straight to one mode. Works
  // for any device type (not just KYD) - "same" is only offered when the selection is uniform
  // type, since a single settings form can't represent several types at once.
  bulkEditTrackersDirectly() {
    const trackers = this.selectedTrackersForBulk;
    if (!trackers.length) return;

    const choiceRef = this.dialog.open(KydConfigureChoiceDialogComponent, {
      width: '450px',
      data: { count: trackers.length, context: 'edit', allowSame: this.selectionHasUniformType(trackers) },
      restoreFocus: false
    });

    choiceRef.afterClosed().subscribe(choice => {
      this.endMultiSelectTrackers();
      if (choice === 'same') this.bulkConfigureTrackersSameConfig(trackers);
      else if (choice === 'each') this.configureTrackersSequentially([...trackers]);
    });
  }

  private bulkConfigureTrackersSameConfig(trackers: Tracker[]) {
    this.dialog.open(KydBulkConfigDialogComponent, {
      width: '600px',
      data: { trackers },
      restoreFocus: false
    });
  }

  // One tracker at a time, in the same single-tracker editor kyd.component.ts's own
  // configureKydsSequentially() uses - saving advances to the next tracker in the queue;
  // closing without saving stops here, leaving whatever hasn't been reached yet unconfigured.
  private configureTrackersSequentially(queue: Tracker[]) {
    const next = queue.shift();
    if (!next) return;

    const dialogRef = this.dialog.open(KydConfigDialogComponent, {
      width: '600px',
      data: { tracker: next },
      restoreFocus: false
    });

    dialogRef.afterClosed().subscribe((saved: boolean) => {
      if (saved) this.configureTrackersSequentially(queue);
    });
  }

  bulkDeleteTrackers() {
    const trackers = this.selectedTrackersForBulk;
    if (!trackers.length) return;

    this.pop.confirm(this.translate.instant('trackers.confirm_delete_trackers'), (confirm: any) => {
      if (!confirm.ok) return;

      this.trackerService.deleteTrackers({ uuids: trackers.map(t => t.uuid) }).subscribe({
        next: () => {
          this.pop.toast(this.translate.instant('trackers.success.removed_trackers'), 'success', 4000, 'delete');
          const deletedIds = new Set(trackers.map(t => t.id));
          this.trackers = this.trackers.filter(t => !deletedIds.has(t.id));
          this.trackersData = new MatTableDataSource(this.trackers);
          this.endMultiSelectTrackers();
        },
        error: (err: any) => {
          if (err == 'offline') {
            this.pop.warning(this.translate.instant('units.impossible_you_are_offline'));
          } else {
            this.pop.error(this.translate.instant('trackers.error.removing_trackers'));
          }
          console.error('[Resources] failed to bulk delete trackers', err);
        }
      });
    });
  }

  // Read-only, works for any device type - unlike apply-template/edit, there's nothing KYD-
  // specific about just looking at what's already configured.
  bulkViewTrackerSettings() {
    const trackers = this.selectedTrackersForBulk;
    if (!trackers.length) return;

    this.dialog.open(TrackerSettingsOverviewDialogComponent, {
      width: '600px',
      data: { trackers },
      restoreFocus: false
    });
  }

  setSchedule(hour: any, day: string | number, type: string) {
    if (type == 'startTime') {
      this.currentUnit.schedule[day][0] = hour;
      this.currentUnit.schedule[day][1] ? this.currentUnit.schedule[day][1] : this.currentUnit.schedule[day][1] = '23:59'
    } else {
      this.currentUnit.schedule[day][1] = hour;
      this.currentUnit.schedule[day][0] ? this.currentUnit.schedule[day][0] : this.currentUnit.schedule[day][0] = "00:00"
    }

    this.updateUnit();
  }

  updateGeometry(event: any) {
    if (event.value != 'none' && event.value != 'Crane') {
      this.currentUnit.collider = {
        offset: {
          x: 0,
          y: 0,
          z: 0
        },
        geometry: event.value,
        topradius: 1,
        bottomradius: 1,
        height: 1,
        length: 1,
        width: 1,
        radius: 1,
        hookMargin: 0
      }
    }

    if (event.value == "Crane" || event.value == "Crane hook")
      this.updateHeight[this.currentUnit.id] = true;

    this.updateUnit();
  }

  updateUnit(event: any = null, fieldName: string | null = null) {
    if (this.currentUnit.collider.extrapolationCoef < 1) {
      this.currentUnit.collider.extrapolationCoef = 1
    }

    if (this.currentUnit.collider.extrapolationCoef > 20) {
      this.currentUnit.collider.extrapolationCoef = 20
    }

    let nUnit = { ...this.currentUnit };
    let geometry = nUnit.collider.geometry;

    if (event) {
      const inputElement = event.target as HTMLInputElement || event as any;

      if (inputElement.name)
        fieldName = inputElement.name;

      const newValue = inputElement.value;

      if (fieldName && this.dynamicFields.map(f => "lastPosition." + f).includes(fieldName)) {
        if (!this.updatedFields[this.currentUnit.id])
          this.updatedFields[this.currentUnit.id] = {};
        this.updatedFields[this.currentUnit.id][fieldName] = parseInt(newValue);
      }
    }

    if (fieldName && ["lastPosition.z", "collider.hookMargin", "crane.height", "crane.trackerOnTop"].includes(fieldName)
      && (geometry == "Crane hook" || geometry == "Crane"))
      this.updateHeight[this.currentUnit.id] = true;

    this.errorName = false;
    nUnit.picture = nUnit.icon;
    nUnit.trackerIds = this.currentUnit.trackerIds.filter((t: string[]) => t);
    nUnit.trackers = nUnit.trackerIds;
    let trackers: Tracker[] = [];
    this.units.forEach((u: Unit) => {
      if (u.id == nUnit.id) {
        u.trackers = [];
        u.trackerIds = [];

        nUnit.trackerIds.forEach((t: string[]) => {
          let tracker = this.trackers.find(_t => _t.id == t);

          if (tracker) {
            u.trackers.push(tracker);

            if (u.trackerIds && u.trackerIds.length)
              u.trackerIds.push(tracker.id);

            trackers.push(tracker);
          }
        });
      }
    })

    this.currentUnit.trackers = trackers;
    this.selectAvalaibleTrackers();

    this.unitService.updateUnit(nUnit).pipe(takeUntil(this.destroy$)).subscribe({
      next: res => {
        if (this.updateHeight[this.currentUnit.id])
          nUnit.updateHeight = true;

        this.isSelectIcon = false;

        if (this.updateHeight[this.currentUnit.id]) {
          delete this.currentUnit.updateHeight;
          this.updateHeight[this.currentUnit.id] = false;
        }
      },
      error: err => {
        console.error('❌ Update failed:', err);
      }
    });
  }

  // `silent` skips this method's own success toast, for a caller that already shows its own
  // for the same user action and would otherwise stack two.
  updateTracker(changes: Record<string, string> = {}, silent: boolean = false) {
    // A connection attempt from the mobile app is in flight for this exact tracker - editing
    // is already greyed out/blocked in the template (see isCurrentTrackerConnecting), this is
    // the same guard applied directly in case this is ever reached another way.
    if (this.isCurrentTrackerConnecting) {
      this.currentTracker.name = this.lastConfirmedTrackerName;
      return;
    }

    if (!this.currentTracker.name) {
      this.pop.toast(this.translate.instant('trackers.no_name_error'), "danger")
      this.currentTracker.name = this.lastConfirmedTrackerName;
      return;
    };

    // "KYD" is what identifies a tracker as one everywhere in the app (see kyd.component.ts's
    // own kydTrackers filter, mirrored here) - renaming it away from here (Ressources >
    // Dispositifs, which edits the very same Tracker record the KYD tab's own rename dialog
    // does) would make an otherwise perfectly good KYD invisible to every KYD-specific screen
    // just as surely as doing it from there. kyd-config-dialog.component.ts's confirmRename()
    // already blocks this from the KYD tab; this closes the same gap from this tab.
    const isKyd = this.currentTracker.type === 'KYD' || this.currentTracker.settings?.type === 'KYD';
    if (isKyd && !this.currentTracker.name.toUpperCase().includes('KYD')) {
      this.pop.toast(this.translate.instant('trackers.name_must_contain_kyd'), "danger");
      // currentTracker is a direct reference into this.trackers/the table row (see editItem()) -
      // ngModel had already written the rejected name into it before this ever ran, so without
      // reverting it here the row/field kept showing the rejected name as if it had saved, even
      // though the PUT below never fired.
      this.currentTracker.name = this.lastConfirmedTrackerName;
      return;
    }

    let tracker: PutTracker = {
      id: this.currentTracker.id,
      name: this.currentTracker.name,
      uuid: this.currentTracker.uuid,
      settings: this.currentTracker.settings,
      settingsConfirmed: this.currentTracker.settingsConfirmed,
      offlineDelay: this.currentTracker.offlineDelay,
      description: this.currentTracker.description,
      version: this.currentTracker.version,
      comment: this.currentTracker.comment,
      project: this.tools.toId(this.currentTracker.project),
      config: this.tools.toId(this.currentTracker.config),
    };

    // currentTracker.type is kept in sync with the config form's own (unlocked, for a
    // never-yet-typed tracker) type select by pushDeviceDraft() - that's now the primary way a
    // tracker gets a type from this tab (trackers-modal.component.ts's creation form is just
    // name/serial). The config-link fallbacks below are legacy paths kept for anything that
    // still sets tracker.config from elsewhere (edit-tracker-modal.component.ts, set-config-
    // modal.component.ts - neither of which this tab uses).
    if (this.currentTracker.type) {
      tracker.type = this.currentTracker.type;
    } else if (this.currentTracker.config?.type) {
      tracker.type = this.currentTracker.config.type;
    } else {
      this.trackerTool.resolveTrackerType(tracker);
    }

    // The isKyd guard above already caught (and returned out on) an ALREADY-KYD tracker being
    // renamed away from "KYD" - so reaching here with a KYD type and a name without it only ever
    // means the tracker is becoming one for the first time in this very save, which gets the
    // same auto-append treatment as kyd-bulk-add-dialog.component.ts's own creation flow rather
    // than being blocked like an actual rename would be.
    if (tracker.type === 'KYD' && tracker.name && !tracker.name.toUpperCase().includes('KYD')) {
      tracker.name = `${tracker.name} KYD`;
      this.currentTracker.name = tracker.name;
    }

    const projects = [
      ...(this.currentTracker.projects ?? []).map(p => this.tools.toId(p)),
      tracker.project
    ].filter(Boolean) as string[];

    tracker.projects = [...new Set(projects)];

    this.trackerService.setTracker(tracker).pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.trackersData.data = [...this.trackers];
      this.lastConfirmedTrackerName = tracker.name ?? '';
      if (!silent) this.pop.success(this.translate.instant("trackers.edit_success"));
    }, err => {
      console.error(err);
      this.pop.error(err.error.message)
    });
  }

  selectAvalaibleTrackers() {
    this.availableTrackers = [];
    this.usedTrackers = [];
    this.usedTrackerIds = [];

    this.units.forEach(unit => {
      unit.trackerIds = unit.trackers.map((t: Tracker) => t.id || t);

      unit.trackers.forEach((tracker: Tracker) => {
        this.usedTrackerIds.push(tracker.id);
      })
    })

    this.trackers.forEach(t => {
      if (this.usedTrackerIds.indexOf(t.id) == -1 || (this.currentUnit.trackerIds && this.currentUnit.trackerIds.indexOf(t.id) >= 0)) {
        this.availableTrackers.push(t);
      } else {
        this.usedTrackers.push(t);
      }
    });
  }

  reloadUnitsList() {
    this.loading = false;
    this.getData();
  }

  add(type: string) {
    if (type == "unit") {
      this.resourcesModal()
    }

    if (type == "tracker") {
      this.trackersModal();
    }
  }

  resourcesModal() {
    const modal = this.dialog.open(ResourcesModalComponent, {
      width: '800px',
      data: {
        colors: this.colors,
        trackers: this.trackers,
        usedTrackerIds: this.usedTrackerIds,
        unitTypeArray: this.unitTypeArray,
        resourceNames: this.unitNames,
        user: this.user(),
        unitIcons: this.unitIcons
      }
    });

    modal.afterClosed().subscribe(res => {
      if (!res) this.getData();
    })
  }

  trackersModal() {
    // A fresh object, never this.currentTracker: this modal only ever creates a NEW tracker
    // (see add()) - reusing the currently-selected one leaked its id/uuid/settings into the
    // "add" payload, so the backend's uuid uniqueness check rejected every attempt with
    // "Tracker already exist" regardless of the name/serial actually typed.
    const modal = this.dialog.open(TrackersModalComponent, {
      width: '600px',
      data: { tracker: { name: '', serial: '' }, user: this.user(), currentProject: this.currentProject }
    });

    modal.afterClosed().subscribe(res => {
      if (!res) this.getData();
    })
  }

  onTrackerChanged(row: any) {
    row.lastUpdate = new Date().toISOString();
    delete row._unit;

    this.trackerService.setTracker(row).pipe(takeUntil(this.destroy$)).subscribe({
      next: res => { },
      error: err => { console.error(err) }
    })
  }

  initSchedule() {
    if (!this.currentUnit.schedule) return;
    for (const [key, value] of Object.entries(this.currentUnit.schedule)) {
      const e = (value as string).toString().split(',');

      if (!e[0]) e[0] = '00:00';
      if (!e[1]) e[1] = '23:59';

      this.schedule[key] = [];
      this.schedule[key][0] = e[0];
      this.schedule[key][1] = e[1];
    }
  }

  getXYFromLatLonAlt2(lon: number, lat: number, alt: number) {
    let r = 6378137 //earth radius in meters
    let x = (r + alt) * (lon * Math.PI / 180) * Math.cos(lat * Math.PI / 180);
    let y = (r + alt) * (lat * Math.PI / 180);

    return [x, y];
  }

  distanceVector(v1: number[], v2: number[]) {
    var dx = (v1[0] ?? 0) - (v2[0] ?? 0);
    var dy = (v1[1] ?? 0) - (v2[1] ?? 0);
    var dz = (v1[2] ?? 0) - (v2[2] ?? 0);

    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  handleDateClick(arg: { dateStr: string; }) {
    alert('date click! ' + arg.dateStr)
  }

  openTrackerPositionModal() {
    const modal = this.dialog.open(TrackerPositionComponent, {
      width: '440px',
      data: { unit: this.currentUnit, trackers: this.trackers, accuracy: this.currentProject().geolocation.accuracy, duration: this.currentProject().geolocation.duration }
    });

    modal.afterClosed().subscribe(res => {
      if (res) {
        this.currentUnit.lastPosition = res;
      }
    });
  }

  // this.currentTracker.connecting/connectingAt are kept live-updated above (see the
  // getTrackers() subscription's own merge block), so this stays accurate for the whole time
  // the sidepanel is open, not just at the moment it was opened.
  get isCurrentTrackerConnecting(): boolean {
    return this.trackerConfigTool.isConnecting(this.currentTracker);
  }

  // --- All device types: the config form edits the tracker's own settings directly, starting
  // from its linked template's defaults (see TrackerTemplateEditor).
  private refreshDeviceDraft() {
    this.deviceEditor.loadTracker(this.currentTracker);
  }

  saveDeviceDraft() {
    if (this.isCurrentTrackerConnecting) return;
    this.deviceEditor.save(() => this.pushDeviceDraft());
  }

  // Saving as a template alone never touches the tracker - offers to apply it right away;
  // declining resets the form back to what the tracker actually has.
  saveDeviceDraftAsTemplate() {
    if (this.isCurrentTrackerConnecting) return;
    this.deviceEditor.saveAsTemplate(() =>
      this.pop.confirm(this.translate.instant('trackers.apply_template_to_sensor_confirm'), (confirm: any) => {
        if (confirm.ok) this.pushDeviceDraft();
        else this.refreshDeviceDraft();
      })
    );
  }

  private pushDeviceDraft() {
    const { type, settings, offlineDelay } = this.deviceEditor.draft;
    const configId = this.deviceEditor.appliedTemplateId;

    this.currentTracker.type = type;
    this.currentTracker.settings = this.trackerConfigTool.mapFlatToNested(settings, type ?? 'Unit', this.currentTracker.settings);
    // The backend ignores an empty `config`, so there's no way (nor need) to unlink one from here.
    if (configId) this.currentTracker.config = configId as any;
    // Not on the sensor yet - only the mobile app sets this back to true after a real BLE write.
    if (type === 'KYD') this.currentTracker.settingsConfirmed = false;
    this.currentTracker.offlineDelay = offlineDelay;
    this.updateTracker();
  }

  copy(content: string) {
    this.uiTool.copy(content);
  }

  // `trackerUuid` is the Tracker.uuid, not its Mongo _id — Intervention.trackers stores uuids.
  private loadLinkedInterventions(trackerUuid: string): void {
    if (!trackerUuid) {
      this.linkedInterventions = [];
      return;
    }

    this.loadingLinkedInterventions = true;

    this.interventionService.searchAndCountInterventions({ trackers: trackerUuid, sort: '-createdDate', limit: 50 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.linkedInterventions = res?.results ?? [];
          this.loadingLinkedInterventions = false;
        },
        error: (err) => {
          console.error('[Resources] Failed to load interventions linked to tracker:', err);
          this.linkedInterventions = [];
          this.loadingLinkedInterventions = false;
        },
      });
  }

  // Opens the Interventions tab with its creation drawer pre-linked to the currently-open
  // Dispositif, rather than duplicating that drawer's form logic here.
  addInterventionForTracker(): void {
    if (!this.currentTracker?.uuid) return;
    this.router.navigate(['/project/interventions'], { queryParams: { add: 'true', tracker: this.currentTracker.uuid } });
  }

  openLinkedIntervention(interventionId: string): void {
    this.router.navigate(['/project/interventions'], { queryParams: { edit: interventionId } });
  }
}

